import React, { useState, useEffect } from "react";
import { AlertCircle } from "lucide-react";
import PageHeader from "../../components/PageHeader";
import DataTable from "../../components/DataTable";
import StatusBadge from "../../components/StatusBadge";
import { listFinancePlans } from "../../services/adminFinance";

const Plans = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [plans, setPlans] = useState([]);
  const [pagination, setPagination] = useState({ pageNumber: 1, pageSize: 20, total: 0, totalPages: 1 });
  const [page, setPage] = useState(0);

  useEffect(() => {
    let isMounted = true;
    const load = async () => {
      setLoading(true);
      try {
        const data = await listFinancePlans({ pageNumber: page + 1, pageSize: 20 });
        if (isMounted) {
          setPlans(data.items || []);
          setPagination(data.pagination || {});
        }
      } catch (err) {
        if (isMounted) setError(err.message || "Erro ao carregar planos");
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    load();
    return () => { isMounted = false; };
  }, [page]);

  const columns = [
    { header: "ID", accessor: "id", render: (r) => <span className="font-mono text-purple-400">#{r.id}</span> },
    { header: "Nome do Plano", accessor: "name", render: (r) => <span className="font-bold text-gray-100">{r.name}</span> },
    {
      header: "Valor (R$)",
      render: (r) =>
        r.amount !== undefined
          ? new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(Number(r.amount))
          : "—"
    },
    { header: "Recorrência", render: (r) => r.recurrence || r.billingCycle || "Mensal" },
    { header: "Usuários", render: (r) => r.users ?? "—" },
    { header: "Conexões", render: (r) => r.connections ?? "—" },
    { header: "Filas", render: (r) => r.queues ?? "—" },
    {
      header: "Público",
      render: (r) => <StatusBadge status={r.isPublic !== false ? "active" : "inactive"} label={r.isPublic !== false ? "Sim" : "Não"} />
    },
    {
      header: "Criado em",
      render: (r) => (r.createdAt ? new Date(r.createdAt).toLocaleDateString("pt-BR") : "—")
    }
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        breadcrumb="Comercial & Financeiro"
        title="Planos & Tarifas"
        subtitle={`${pagination.total || 0} plano(s) cadastrados na plataforma`}
      />

      {error && (
        <div className="p-4 rounded-2xl bg-rose-500/10 border border-rose-500/20 text-rose-300 text-xs font-semibold flex items-center gap-3">
          <AlertCircle size={18} /> {error}
        </div>
      )}

      <DataTable
        columns={columns}
        data={plans}
        loading={loading}
        emptyTitle="Nenhum plano encontrado"
        emptyDescription="Cadastre novos planos comerciais para visualizá-los aqui."
      />

      {pagination.totalPages > 1 && (
        <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400 pt-1">
          <span>Página {pagination.pageNumber} de {pagination.totalPages} ({pagination.total} planos)</span>
          <div className="flex gap-2">
            <button
              disabled={pagination.pageNumber <= 1}
              onClick={() => setPage(pagination.pageNumber - 2)}
              className="px-3 py-1 rounded-lg bg-gray-100 dark:bg-white/5 disabled:opacity-30 hover:bg-gray-200 dark:hover:bg-white/10"
            >
              Anterior
            </button>
            <button
              disabled={pagination.pageNumber >= pagination.totalPages}
              onClick={() => setPage(pagination.pageNumber)}
              className="px-3 py-1 rounded-lg bg-gray-100 dark:bg-white/5 disabled:opacity-30 hover:bg-gray-200 dark:hover:bg-white/10"
            >
              Próxima
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Plans;
